import { useCallback, useEffect, useState } from 'react'
import { errorMessage, listSettings, updateSetting } from '../../services/admin'
import { faDigits } from '../../lib/numerals'
import { formatShortDatetime } from './dates'
import { fromEditorText, kindOf, toEditorText } from './settingsEditor'
import { settingConfirmNote, settingNote } from './settingNotes'
import {
  groupSettings,
  isOwnerSwitch,
  keySpec,
  ownerConfirmMatches,
  ownerSwitchNote,
  parseRanks,
  ranksFromText,
  ranksToText,
  validateSettingValue,
} from './rewardSettings'

// ─── Settings tab (admin-settings, X5-p) ──────────────────────────────────────

/**
 * The panel's own note for a key, when it has one (V9-p): the registry's
 * English `description` says what the key is, this says what happens when it
 * changes. Keys without a note render exactly as before.
 */
function KeyNote({ note }) {
  if (!note) return null
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2 text-xs leading-6 text-slate-600">
      <span className="block font-medium text-slate-700">{note.title}</span>
      <ul className="list-disc space-y-0.5 ps-5">
        {note.points.map(point => <li key={point}>{point}</li>)}
      </ul>
    </div>
  )
}

function Actor({ updatedAt, updatedById }) {
  if (!updatedAt) return <span className="text-xs text-slate-400">هرگز تغییر نکرده — پیش‌فرض کد</span>
  return (
    <span className="text-xs text-slate-400" dir="ltr">
      {formatShortDatetime(updatedAt)} · {updatedById ? `admin ${updatedById.slice(0, 8)}` : 'کلید مشترک'}
    </span>
  )
}

/**
 * The standing warning of an owner switch (V6-p): shown on the row before
 * anything is clicked, so nobody flips it thinking it is a tuning knob.
 */
function OwnerSwitchWarning({ settingKey }) {
  const note = ownerSwitchNote(settingKey)
  if (!note) return null
  return (
    <div className="rounded-lg border border-warn bg-warn-tint px-3 py-2 text-xs leading-6 text-warn-ink" role="note">
      <span className="block font-semibold">کلید مالک — فقط با تصمیم صریح مالک تغییر دهید.</span>
      <span className="block">{note}</span>
      <span className="block">ذخیره فقط پس از نوشتن نام کلید انجام می‌شود.</span>
    </div>
  )
}

const inputCls = 'rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:bg-slate-800'
const smallBtn = 'rounded-lg border border-slate-300 px-2 py-1 text-xs text-ink-2 hover:bg-slate-50 disabled:opacity-40'

/**
 * A prize list (`leaderboard.*PrizeGems`) as one input per rank instead of a
 * JSON textarea: add a rank at the end, drop the last one. `text` is the
 * row's editor state — a JSON array of the inputs' strings (ranksToText).
 */
export function RanksEditor({ text, onChange, spec }) {
  const ranks = ranksFromText(text)
  function set(index, raw) {
    const next = ranks.slice()
    next[index] = raw
    onChange(JSON.stringify(next))
  }
  return (
    <div className="space-y-2">
      {ranks.length === 0 && <p className="text-xs text-ink-3">هیچ رتبه‌ای جایزه ندارد.</p>}
      <div className="flex flex-wrap gap-3">
        {ranks.map((raw, index) => (
          <label key={index} className="flex items-center gap-1 text-xs text-ink-2">
            <span>رتبهٔ {faDigits(index + 1)}</span>
            <input type="number" min={spec.min} max={spec.max} step="1" value={raw} onChange={e => set(index, e.target.value)}
              className={`w-20 ${inputCls}`} dir="ltr" aria-label={`جایزهٔ رتبهٔ ${index + 1}`} />
            {spec.unit && <span>{spec.unit}</span>}
          </label>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" disabled={ranks.length >= spec.maxRanks} onClick={() => onChange(JSON.stringify([...ranks, '0']))} className={smallBtn}>
          + رتبهٔ بعد
        </button>
        <button type="button" disabled={ranks.length === 0} onClick={() => onChange(JSON.stringify(ranks.slice(0, -1)))} className={smallBtn}>
          − حذف رتبهٔ آخر
        </button>
      </div>
    </div>
  )
}

/**
 * One setting: key, description, default, current value, an editor sized to
 * its type, and a save button. A sensitive key (`planB.enabled`, and the
 * owner's switches when they move here) does not save on the first click: the
 * button turns into an inline confirm row, so the value under it is still
 * visible while the admin decides — nothing pops over the page.
 *
 * V6-p: a key with a spec in rewardSettings.js also gets a Persian label and
 * help, its bounds checked before the wire, and — for a prize list — a
 * per-rank editor. An owner switch (`isOwnerSwitch`) always confirms, shows a
 * standing warning, and saves only once the key's name is typed back.
 */
export function SettingRow({ item, onSaved }) {
  const spec = keySpec(item.key)
  const ranksKind = spec?.type === 'ranks'
  const kind = ranksKind ? 'ranks' : kindOf(item.default)
  const note = settingNote(item.key)
  const owner = isOwnerSwitch(item.key)
  const needsConfirm = item.sensitive || owner
  const textOf = value => (ranksKind ? ranksToText(value) : toEditorText(kind, value))
  const [text, setText] = useState(() => textOf(item.value))
  const [confirming, setConfirming] = useState(false)
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [saved, setSaved] = useState(false)

  const dirty = text !== textOf(item.value)
  // What confirming would actually cause, for the value in the editor right
  // now — a sensitive switch reads differently on the way on than on the way off.
  const confirmNote = settingConfirmNote(item.key, text)
  const typedOk = !owner || ownerConfirmMatches(item.key, typed)

  /** The editor's text as the value to send, checked against the key's bounds; throws in Persian. */
  function valueToSend() {
    const value = ranksKind ? parseRanks(text) : fromEditorText(kind, text)
    return validateSettingValue(item.key, value)
  }

  function cancelConfirm() {
    setConfirming(false)
    setTyped('')
  }

  async function save() {
    setErr(''); setSaved(false)
    // What cannot go on the wire at all is refused here, in the helper's own
    // words; errorMessage() is for what the server answered.
    let value
    try { value = valueToSend() }
    catch (ex) { setErr(ex.message); cancelConfirm(); return }
    setBusy(true)
    try {
      const stored = await updateSetting(item.key, value)
      // Show the stored value the way the editor would print it — "12.0"
      // becomes 12, hand-typed JSON becomes pretty JSON — so the row is
      // clean again and the button disables until the next real change.
      setText(textOf(stored))
      cancelConfirm()
      setSaved(true)
      onSaved({ ...item, value: stored, updatedAt: new Date().toISOString() })
    } catch (ex) { setErr(errorMessage(ex)) }
    finally { setBusy(false) }
  }

  function submit(e) {
    e.preventDefault()
    if (needsConfirm && !confirming) {
      // Check the value first, so the confirm row never offers what the save would refuse.
      setErr('')
      try { valueToSend() } catch (ex) { setErr(ex.message); return }
      setConfirming(true)
      return
    }
    if (!typedOk) return
    save()
  }

  const shownValue = kind === 'ranks' ? JSON.stringify(ranksFromText(text).map(Number)) : text

  return (
    <form onSubmit={submit} className="space-y-2 rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm dark:bg-slate-800">
      {spec?.label && <div className="text-sm font-semibold text-ink">{spec.label}</div>}
      <div className="flex flex-wrap items-center gap-2">
        <code className="rounded bg-slate-100 px-2 py-0.5 text-sm font-semibold text-slate-800 dark:bg-surface-2 dark:text-ink" dir="ltr">{item.key}</code>
        {owner && (
          <span className="rounded-full bg-bad-tint px-2 py-0.5 text-xs font-medium text-bad-ink">کلید مالک</span>
        )}
        {item.sensitive && (
          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">حساس — با تأیید</span>
        )}
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500 dark:bg-surface-2 dark:text-ink-2">{kind}</span>
        <span className="ms-auto"><Actor updatedAt={item.updatedAt} updatedById={item.updatedById} /></span>
      </div>
      {spec?.help && <p className="text-xs leading-6 text-ink-2">{spec.help}</p>}
      <p className="text-xs text-slate-500" dir="auto">{item.description}</p>
      <KeyNote note={note} />
      {owner && <OwnerSwitchWarning settingKey={item.key} />}
      <div className="flex flex-wrap items-end gap-3">
        <div className="block min-w-48 flex-1">
          <span className="mb-1 block text-xs font-medium text-slate-600 dark:text-ink-2">
            مقدار فعلی{spec?.unit && kind !== 'ranks' ? ` (${spec.unit})` : ''}
          </span>
          {kind === 'boolean' && (
            <select value={text} onChange={e => setText(e.target.value)} className={`w-full ${inputCls}`} dir="ltr" aria-label={item.key}>
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          )}
          {kind === 'number' && (
            <input type="number" step={spec?.type === 'int' ? '1' : 'any'} min={spec?.min} max={spec?.max} value={text}
              onChange={e => setText(e.target.value)} className={`w-full ${inputCls}`} dir="ltr" aria-label={item.key} />
          )}
          {kind === 'string' && (
            <input type="text" value={text} onChange={e => setText(e.target.value)} className={`w-full ${inputCls}`} dir="ltr" aria-label={item.key} />
          )}
          {kind === 'json' && (
            <textarea value={text} onChange={e => setText(e.target.value)} rows={4} className={`w-full font-mono ${inputCls}`} dir="ltr" aria-label={item.key} />
          )}
          {kind === 'ranks' && <RanksEditor text={text} onChange={setText} spec={spec} />}
          {spec?.min != null && spec?.max != null && (
            <span className="mt-1 block text-xs text-ink-3">بازهٔ مجاز: {faDigits(spec.min)} تا {faDigits(spec.max)}</span>
          )}
        </div>
        <div className="text-xs text-slate-500">
          <span className="mb-1 block font-medium text-slate-600 dark:text-ink-2">پیش‌فرض</span>
          <code dir="ltr">{toEditorText(kindOf(item.default), item.default)}</code>
        </div>
        {!confirming && (
          <button type="submit" disabled={busy || !dirty}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50">
            {busy ? 'در حال ذخیره…' : 'ذخیره'}
          </button>
        )}
      </div>
      {confirming && (
        <div className={`flex flex-wrap items-center gap-2 rounded-lg px-3 py-2 text-sm ${owner ? 'bg-bad-tint text-bad-ink' : 'bg-amber-50 text-amber-800'}`}>
          <span>
            {owner ? 'این کلید مالک است. ' : 'این کلید حساس است. '}
            <code dir="ltr">{item.key}</code> به <code dir="ltr">{shownValue}</code> تغییر کند؟
          </span>
          {confirmNote && <span className="w-full text-xs leading-6">{confirmNote}</span>}
          {owner && (
            <label className="flex w-full flex-wrap items-center gap-2 text-xs">
              <span>برای تأیید، نام کلید (<code dir="ltr">{item.key}</code>) را دقیقاً بنویسید:</span>
              <input type="text" value={typed} onChange={e => setTyped(e.target.value)} dir="ltr" placeholder={item.key}
                className={`w-64 ${inputCls}`} aria-label="نام کلید برای تأیید" autoComplete="off" />
            </label>
          )}
          <button type="submit" disabled={busy || !typedOk}
            className="rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50">
            {busy ? 'در حال ذخیره…' : 'بله، تغییر بده'}
          </button>
          <button type="button" disabled={busy} onClick={cancelConfirm}
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 dark:text-ink-2">
            انصراف
          </button>
        </div>
      )}
      {err && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-bad-tint dark:text-bad-ink">{err}</div>}
      {saved && !dirty && <div className="rounded-lg bg-green-50 px-4 py-2 text-sm text-green-700 dark:bg-good-tint dark:text-good-ink">ذخیره شد.</div>}
    </form>
  )
}

/** The list, given its items — what the tab renders once loaded, and what the test renders directly. */
export function SettingsList({ items, onSaved }) {
  if (items.length === 0) {
    return <p className="py-16 text-center text-sm text-slate-400">هیچ تنظیم قابل‌ویرایشی ثبت نشده.</p>
  }
  // V6-p: the XP / gem / leaderboard keys under Persian headings; every other key as before, under «سایر تنظیمات».
  const groups = groupSettings(items)
  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        هر کلید یک پیش‌فرض در کد دارد؛ مقدار ذخیره‌شده روی آن می‌نشیند و سرویس آن را ظرف چند ثانیه (کش کوتاه) می‌بیند — بدون ساخت یا استقرار دوباره.
      </p>
      {groups.length > 1 && (
        <nav className="flex flex-wrap gap-2 text-xs" aria-label="گروه‌های تنظیمات">
          {groups.map(group => (
            <a key={group.id} href={`#settings-${group.id}`} className="rounded-full bg-slate-100 px-3 py-1 text-ink-2 hover:bg-brand-50 dark:bg-surface-2">
              {group.title} ({faDigits(group.items.length)})
            </a>
          ))}
        </nav>
      )}
      {groups.map(group => (
        <section key={group.id} id={`settings-${group.id}`} className="space-y-3" aria-labelledby={`settings-${group.id}-title`}>
          <div className="border-b border-line pb-1 pt-3">
            <h3 id={`settings-${group.id}-title`} className="text-sm font-semibold text-ink">{group.title}</h3>
            {group.help && <p className="mt-1 text-xs leading-6 text-ink-2">{group.help}</p>}
          </div>
          {group.items.map(item => (
            <SettingRow key={item.key} item={item} onSaved={onSaved} />
          ))}
        </section>
      ))}
    </div>
  )
}

/** The settings tab (X5-p): loads `admin-settings_detail`, renders one row per registered key. */
export function SettingsTab() {
  const [items, setItems] = useState(null)
  const [err, setErr] = useState('')

  const load = useCallback(async () => {
    setErr('')
    try { setItems(await listSettings()) }
    catch (ex) { setErr(errorMessage(ex)) }
  }, [])

  useEffect(() => { load() }, [load])

  function replace(updated) {
    setItems(list => list.map(i => i.key === updated.key ? updated : i))
  }

  if (err) {
    return (
      <div className="space-y-3">
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{err}</div>
        <button onClick={load} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50">تلاش دوباره</button>
      </div>
    )
  }
  if (items === null) return <p className="py-16 text-center text-sm text-slate-400">در حال بارگذاری…</p>
  return <SettingsList items={items} onSaved={replace} />
}
